/* Per-(source,event) presentation: turns a notification into an icon + message + in-app route */
import { formatISOAsTimeAgo } from 'src/utils/date-time-utilities';
import { ACCESS_MANAGEMENT_SOURCE, AccessRequestNotification, parseAccessRequestNotification, XptNotification } from './notificationModels';

/**
 * Semantic weight of a notification, declared by the presenter.
 *
 * Kept separate from `iconName` so a UI layer can pick its own iconography from
 * intent instead of reverse-engineering a Cloudscape icon name.
 */
export type NotificationSeverity = 'success' | 'critical' | 'pending' | 'unknown';

/**
 * Where a notification's action leads.
 *
 * A reviewer's item cannot be expressed as a fixed path: the business-group access page is keyed
 * by the group's short description, which the payload does not carry (it has the numeric id). So
 * the target names the business group and the presenter stays pure, leaving the caller to resolve
 * the short description from the store via resolveNotificationPath.
 */
export type NotificationTarget =
  | { kind: 'path'; path: string }
  | { kind: 'businessGroupAccess'; businessGroupId: number; tab: 'pending-requests' | 'request-history' };

export interface NotificationPresentation {
  iconName: string; // Cloudscape icon name
  severity: NotificationSeverity;
  /** Short scannable summary, e.g. "Access request approved". Sentence case, no trailing period. */
  title: string;
  /** Full detail line, e.g. who requested what for which business group. */
  message: string;
  /** Where the row's action goes. Null when the item is not click-through. */
  target: NotificationTarget | null;
  /**
   * How long ago the request was submitted, e.g. "5 minutes ago".
   *
   * Empty when the payload carries no timestamp — the Notification type has none of its own, so
   * this depends on the publisher forwarding the request's created_at.
   */
  timeAgo: string;
}

/** `owns` decides whether this presenter handles the notification
 * `present` renders it (only called when `owns` returned true).
 */
interface NotificationPresenter {
  owns: (notification: XptNotification) => boolean;
  present: (notification: XptNotification) => NotificationPresentation;
}

/** Fallback for notifications no presenter owns, or whose payload failed to parse. We still
 * show the item but with a "failed to load" message and no route, so the user knows it's broken/
 * The console.error in the payload parser carries the real diagnostics. */
const genericPresentation = (): NotificationPresentation => ({
  iconName: 'status-warning',
  severity: 'unknown',
  title: 'Notification unavailable',
  message: 'This notification could not be loaded',
  target: null,
  timeAgo: ''
});

// ── Access-management presenter ──────────────────────────────────────────────────────────────────

const nameAlias = (name: string, alias: string): string => `${name} (${alias})`;

/** The effective outcome of a request, or null while still pending. Comes from the item's own `kind`
 * (requester/beneficiary standalone outcome items) OR from `resolution_action` on a reconciled-in-place
 * SUBMITTED item (the FAs' item, whose `kind` stays SUBMITTED). */
const resolutionOf = (req: AccessRequestNotification): 'APPROVED' | 'REJECTED' | 'CANCELLED' | null => {
  if (req.kind !== 'SUBMITTED') {
    return req.kind;
  }
  return req.resolution_action ?? null;
};

const accessRequestMessage = (req: AccessRequestNotification): string => {
  const requester = nameAlias(req.requester_name, req.requester_alias);
  const beneficiary = nameAlias(req.beneficiary_name, req.beneficiary_alias);
  const isSelf = req.requester_alias === req.beneficiary_alias;
  const role = req.requested_role;
  const bg = req.business_group_name;
  const resolution = resolutionOf(req);

  // Still pending (a SUBMITTED item with no resolution yet): describe who requested what.
  if (!resolution) {
    return isSelf ? `${requester} requested ${role} for ${bg}` : `${requester} requested ${role} for ${bg} on behalf of ${beneficiary}`;
  }

  const isPartial = resolution === 'APPROVED' && req.is_partial_approval;
  const verb = isPartial ? 'approved with changes' : { APPROVED: 'approved', REJECTED: 'rejected', CANCELLED: 'cancelled' }[resolution];
  const by = req.resolved_by ? ` by ${req.resolved_by_name ? nameAlias(req.resolved_by_name, req.resolved_by) : req.resolved_by}` : '';
  return `${beneficiary}'s ${role} request for ${bg} was ${verb}${by}`;
};

/** Icon reflecting the request's outcome: approved -> tick, rejected/cancelled -> cross, still
 * pending -> pending clock. */
const iconForResolution = (resolution: 'APPROVED' | 'REJECTED' | 'CANCELLED' | null): string => {
  switch (resolution) {
    case 'APPROVED':
      return 'status-positive';
    case 'REJECTED':
    case 'CANCELLED':
      return 'status-negative';
    default:
      return 'status-pending';
  }
};

/**
 * Short summary line for the notification row. The detailed who/what/where stays in
 * `accessRequestMessage` — this is the scannable heading above it.
 */
const accessRequestTitle = (req: AccessRequestNotification): string => {
  const resolution = resolutionOf(req);
  if (!resolution) {
    // A SUBMITTED item with no resolution is the reviewer's "needs your review" item.
    return 'Access request needs review';
  }
  if (resolution === 'APPROVED') {
    return req.is_partial_approval ? 'Access request approved with changes' : 'Access request approved';
  }
  return resolution === 'REJECTED' ? 'Access request rejected' : 'Access request cancelled';
};

/** Semantic counterpart of `iconForResolution`, for UI layers that pick their own icons. */
const severityForResolution = (resolution: 'APPROVED' | 'REJECTED' | 'CANCELLED' | null): NotificationSeverity => {
  switch (resolution) {
    case 'APPROVED':
      return 'success';
    case 'REJECTED':
    case 'CANCELLED':
      return 'critical';
    default:
      return 'pending';
  }
};

const accessRequestPresenter: NotificationPresenter = {
  owns: (n) => n.source === ACCESS_MANAGEMENT_SOURCE,
  present: (n) => {
    const req = parseAccessRequestNotification(n);
    if (!req) {
      return genericPresentation();
    }
    const resolution = resolutionOf(req);
    // A SUBMITTED item is the reviewer's notification, and it points at the request's own
    // BUSINESS GROUP access page rather than the global admin console. The global console is
    // limited to dev-group members and Finance Admins, but a request is reviewable by the group's
    // Finance Owners too — sending them to the global page would land them somewhere they cannot
    // view. The per-group page is gated on Finance Admin OR Finance Owner, so it serves everyone
    // who can act on the request.
    //
    // The tab follows the request's state: while pending it sits in the pending queue, and once
    // resolved it has moved to the request history.
    const target: NotificationTarget = 
      req.kind === 'SUBMITTED'
        ? { kind: 'businessGroupAccess', businessGroupId: req.bg_id, tab: resolution ? 'request-history' : 'pending-requests' }
        : // APPROVED/REJECTED are the requester/beneficiary's own outcome, and their requests page
          // already lists every status.
          { kind: 'path', path: '/my-requests' };
    return {
      iconName: iconForResolution(resolution),
      severity: severityForResolution(resolution),
      title: accessRequestTitle(req),
      message: accessRequestMessage(req),
      target,
      timeAgo: formatISOAsTimeAgo(req.created_at)
    };
  }
};

// ── Registry ─────────────────────────────────────────────────────────────────────────────────────

const PRESENTERS: NotificationPresenter[] = [accessRequestPresenter];

/** Build the presentation for a notification, delegating to the presenter that owns its type. */
export const getNotificationPresentation = (notification: XptNotification): NotificationPresentation => {
  const presenter = PRESENTERS.find((p) => p.owns(notification));
  return presenter ? presenter.present(notification) : genericPresentation();
};

/**
 * Turn a notification target into an in-app path.
 *
 * `resolveBusinessGroupShortDesc` maps a business group's numeric id to the short description the
 * business-group routes are keyed by. It returns undefined when the group is not in the store,
 * which for a business-group target means the viewer has no route to it — the caller then renders
 * the row without an action rather than linking somewhere unreachable.
 */
export const resolveNotificationPath = (
  target: NotificationTarget | null,
  resolveBusinessGroupShortDesc: (businessGroupId: number) => string | undefined
): string | null => {
  if (!target) {
    return null;
  }
  if (target.kind === 'path') {
    return target.path;
  }
  const shortDesc = resolveBusinessGroupShortDesc(target.businessGroupId);
  return shortDesc ? `/${shortDesc}/access-authorization?tab=${target.tab}` : null;
};
